import React, { useCallback, useEffect, useState } from 'react';
import {
    CheckCircleIcon,
    ArrowUturnLeftIcon,
    XCircleIcon,
    MapPinIcon,
    ArrowDownTrayIcon,
    PrinterIcon,
    PlusIcon,
} from '@heroicons/react/24/outline';
import ActivityDrawer from './ActivityDrawer';
import ActivityStatusBadge from './ActivityStatusBadge';
import PlannedVsActualPanel from './PlannedVsActualPanel';
import EvidenceGallery, { resolveMediaUrl } from './EvidenceGallery';
import ObservationList from './ObservationList';
import FieldMiniMap from './FieldMiniMap';
import { fetchReport, approveReport, requestReportCorrection, rejectReport, downloadReportCsv, downloadReportJson, openReportPrintable } from '../../utils/activityApi';
import { canExport } from '../../utils/activityRoles';
import { useAuth } from '../../contexts/AuthContext';
import type { ActivityReview, AssignmentMaterialPlanRow, ReportDetail, ReportMaterialClaim } from './types';

interface ReportReviewDrawerProps {
    open: boolean;
    reportId: number | null;
    onClose: () => void;
    canManage: boolean;
    fields: any[];
    onDataChanged: () => void;
    onCreateFollowUp?: (report: NonNullable<ReportDetail['report']>, dailyReport?: ReportDetail['daily_report']) => void;
}

type ActionKind = 'approve' | 'correct' | 'reject';

const formatDateTime = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const valueRow = (label: string, value?: string | number | null) => (
    <div>
        <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</p>
        <p className={`text-sm ${value === undefined || value === null || value === '' ? 'text-slate-400' : 'font-semibold text-slate-800'}`}>
            {value === undefined || value === null || value === '' ? '—' : value}
        </p>
    </div>
);

const humanize = (value: string) => value.replace(/_/g, ' ');

const materialStatusStyle = (status?: string) => {
    switch (status) {
        case 'verified': return 'bg-emerald-100 text-emerald-800';
        case 'corrected': return 'bg-amber-100 text-amber-800';
        case 'rejected': return 'bg-red-100 text-red-700';
        default: return 'bg-slate-100 text-slate-600';
    }
};

/**
 * Planned versus farmer-reported quantity, per lot, with the photos that prove
 * it. Approving the report is what deducts the lot stock exactly once; this
 * panel never writes anything itself.
 */
const MaterialLotReviewPanel: React.FC<{
    planned?: AssignmentMaterialPlanRow[];
    reported?: ReportMaterialClaim[];
}> = ({ planned, reported }) => {
    const rows = React.useMemo(() => {
        const byLot = new Map<string, {
            lotId: number;
            lotCode?: string;
            productName?: string;
            productType?: string;
            sourceType?: string;
            unit: string;
            plannedQuantity?: number | null;
            rate?: number | null;
            rateUnit?: string | null;
            issued?: number | null;
            claim?: ReportMaterialClaim;
        }>();
        (planned || []).forEach((row) => {
            const key = String(row.material_lot_id);
            byLot.set(key, {
                lotId: Number(row.material_lot_id),
                lotCode: row.lot_code,
                productName: row.product_name,
                productType: row.product_type,
                sourceType: row.source_type,
                unit: row.unit || row.lot_code || 'kg',
                plannedQuantity: row.planned_quantity,
                rate: row.application_rate,
                rateUnit: row.application_rate_unit,
                issued: row.reserved_quantity,
            });
        });
        (reported || []).forEach((claim) => {
            const key = String(claim.material_lot_id);
            const current = byLot.get(key) || {
                lotId: Number(claim.material_lot_id),
                lotCode: claim.lot_code,
                productName: claim.product_name,
                productType: claim.product_type,
                sourceType: claim.source_type,
                unit: claim.unit || 'kg',
                plannedQuantity: claim.planned_quantity ?? null,
            };
            byLot.set(key, { ...current, claim });
        });
        return [...byLot.values()];
    }, [planned, reported]);

    if (!rows.length) return null;

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Material lots</p>
                    <p className="text-xs font-semibold text-slate-500">
                        Planned versus what the farmer reported. Approval deducts each lot once; a retry or a second
                        review never takes the same kilos twice.
                    </p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black text-slate-600">
                    Stock moves on approval only
                </span>
            </div>

            <div className="space-y-3">
                {rows.map((row) => {
                    const claim = row.claim;
                    const photos = Array.isArray(claim?.photo_urls) ? claim!.photo_urls : [];
                    return (
                        <div key={row.lotId} className="rounded-xl border border-slate-200 p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <p className="text-sm font-black text-slate-900">
                                        {row.lotCode || `Lot ${row.lotId}`}
                                        <span className="ml-2 font-semibold text-slate-500">{row.productName || humanize(row.productType || '')}</span>
                                    </p>
                                    <p className="text-[11px] font-semibold text-slate-400">
                                        {row.sourceType === 'farm_produced' ? 'Farm-produced' : 'Purchased'}
                                        {row.rate ? ` · rate ${row.rate}${row.rateUnit ? ` ${row.rateUnit}` : ''}` : ' · no approved rate recorded'}
                                    </p>
                                </div>
                                {claim ? (
                                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${materialStatusStyle(claim.status)}`}>
                                        {claim.status}
                                    </span>
                                ) : (
                                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-800">
                                        no farmer quantity
                                    </span>
                                )}
                            </div>

                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                                {valueRow('Planned', row.plannedQuantity != null ? `${row.plannedQuantity} ${row.unit}` : undefined)}
                                {valueRow('Reported applied', claim ? `${claim.quantity_applied} ${claim.unit}` : undefined)}
                                {valueRow('Returned', claim ? `${claim.quantity_returned || 0} ${claim.unit}` : undefined)}
                                {valueRow('Applied on', claim?.applied_on || undefined)}
                            </div>

                            {claim?.variance_note && (
                                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
                                    {claim.variance_note}
                                </p>
                            )}
                            {claim?.observations && (
                                <p className="mt-2 text-xs font-semibold text-slate-600">{claim.observations}</p>
                            )}

                            {photos.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {photos.map((url) => {
                                        const resolved = resolveMediaUrl(url);
                                        return resolved ? (
                                            <a key={url} href={resolved} target="_blank" rel="noreferrer" className="block">
                                                <img src={resolved} alt={`Evidence for ${row.lotCode || row.lotId}`} className="h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200" />
                                            </a>
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const displayValue = (value: any): string => {
    if (Array.isArray(value)) {
        if (!value.length) return '—';
        if (value.some((item) => item && typeof item === 'object')) return `${value.length} record${value.length === 1 ? '' : 's'}`;
        return value.join(', ');
    }
    if (value && typeof value === 'object') {
        const entries = Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '');
        return entries.length ? entries.map(([key, item]) => `${humanize(key)}: ${item}`).join(', ') : '—';
    }
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return value === undefined || value === null || value === '' ? '—' : String(value);
};

const DailyReportSection: React.FC<{ title: string; data?: Record<string, any>; columns?: 2 | 3 }> = ({ title, data, columns = 2 }) => {
    if (!data) return null;
    const entries = Object.entries(data);
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-emerald-700">{title}</p>
            <div className={`grid gap-x-4 gap-y-3 ${columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {entries.map(([key, value]) => valueRow(humanize(key), displayValue(value)))}
            </div>
        </div>
    );
};

const ReportReviewDrawer: React.FC<ReportReviewDrawerProps> = ({
    open,
    reportId,
    onClose,
    canManage,
    fields,
    onDataChanged,
    onCreateFollowUp,
}) => {
    const [detail, setDetail] = useState<ReportDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [activeAction, setActiveAction] = useState<ActionKind | null>(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const { user } = useAuth();
    const allowExport = canExport(user?.role);

    const load = useCallback(async () => {
        if (!open || !reportId) return;
        setLoading(true);
        setError('');
        setActionError('');
        try {
            const data = await fetchReport(reportId);
            setDetail(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to load report.');
        } finally {
            setLoading(false);
        }
    }, [open, reportId]);

    useEffect(() => {
        if (open) {
            setDetail(null);
            setActiveAction(null);
            setNote('');
            setActionError('');
            load();
        }
    }, [open, load]);

    const report = detail?.report || null;
    const dailyReport = detail?.daily_report;
    const observations = detail?.observations || [];
    const reviews = detail?.reviews || [];

    const canReview = canManage && report && ['submitted', 'awaiting_review', 'resubmitted', 'correction_required'].includes(report.status);

    const runAction = async (kind: ActionKind) => {
        if (!report) return;
        setBusy(true);
        setActionError('');
        try {
            if (kind === 'approve') {
                await approveReport(report.id, note || undefined);
            } else if (kind === 'correct') {
                if (!note.trim()) {
                    setActionError('Correction instructions are required.');
                    setBusy(false);
                    return;
                }
                await requestReportCorrection(report.id, note.trim());
            } else {
                if (!note.trim()) {
                    setActionError('A rejection reason is required.');
                    setBusy(false);
                    return;
                }
                await rejectReport(report.id, note.trim());
            }
            setActiveAction(null);
            setNote('');
            onDataChanged();
            await load();
        } catch (err: any) {
            setActionError(err?.message || 'Action failed.');
        } finally {
            setBusy(false);
        }
    };

    const handleExport = async (kind: 'json' | 'csv' | 'print') => {
        if (!report) return;
        setActionError('');
        try {
            if (kind === 'json') await downloadReportJson(report);
            else if (kind === 'csv') await downloadReportCsv(report);
            else await openReportPrintable(report);
        } catch (err: any) {
            setActionError(err?.message || 'Export failed.');
        }
    };

    const field = report?.field_plot_id ? fields.find((f) => Number(f.id) === Number(report.field_plot_id)) : null;

    const evidenceGroups = report
        ? [
              { label: 'Before', urls: report.before_photo_urls },
              { label: 'During', urls: report.during_photo_urls },
              { label: 'After', urls: report.after_photo_urls },
              { label: 'Additional', urls: report.additional_photo_urls },
          ]
        : [];

    const planned = report
        ? {
              area: report.assignment_planned_area,
              areaUnit: report.assignment_planned_area_unit,
              quantity: report.assignment_planned_quantity,
              quantityUnit: report.assignment_planned_quantity_unit,
              rate: undefined,
              rateUnit: undefined,
              durationMinutes: report.assignment_expected_duration_hours
                  ? Number(report.assignment_expected_duration_hours) * 60
                  : undefined,
              material: report.assignment_material_name,
              materialUnit: report.assignment_material_unit,
          }
        : null;

    const actual = report
        ? {
              area: report.area_completed,
              areaUnit: report.area_unit,
              quantity: report.quantity_used,
              quantityUnit: report.quantity_unit,
              rate: report.calculated_rate,
              rateUnit: undefined,
              durationMinutes: report.total_working_minutes,
              material: report.material_name,
              materialUnit: report.material_unit,
          }
        : null;

    const actionCopy: Record<ActionKind, { title: string; body: string; button: string; placeholder: string; required: boolean; color: string }> = {
        approve: {
            title: 'Approve report',
            body: 'Approve this report and mark the assignment as approved.',
            button: 'Approve report',
            placeholder: 'Optional review comment…',
            required: false,
            color: 'bg-emerald-600 hover:bg-emerald-700 text-white',
        },
        correct: {
            title: 'Request correction',
            body: 'Return the report to the field team with clear instructions.',
            button: 'Request correction',
            placeholder: 'Instructions for the field team (required)…',
            required: true,
            color: 'bg-orange-600 hover:bg-orange-700 text-white',
        },
        reject: {
            title: 'Reject report',
            body: 'Reject the report and the assignment.',
            button: 'Reject report',
            placeholder: 'Rejection reason (required)…',
            required: true,
            color: 'bg-red-600 hover:bg-red-700 text-white',
        },
    };

    return (
        <ActivityDrawer
            open={open}
            onClose={onClose}
            width="2xl"
            title={report ? `Report ${report.report_code}` : 'Report review'}
            subtitle={
                report ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                        <ActivityStatusBadge status={report.status} size="sm" />
                        <span className="text-xs text-slate-400">Submitted {formatDateTime(report.submitted_at)}</span>
                    </span>
                ) : undefined
            }
        >
            {loading && <div className="py-12 text-center text-sm text-slate-500">Loading report…</div>}

            {!loading && error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && report && (
                <div className="space-y-6">
                    {actionError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>
                    )}

                    <div className="rounded-xl border border-slate-200 p-4">
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Assignment</p>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <p className="font-black text-slate-900">{report.assignment_title || report.assignment_code}</p>
                                <p className="text-xs text-slate-500">
                                    {report.assignment_code}
                                    {report.activity_type_name ? ` · ${report.activity_type_name}` : ''}
                                </p>
                            </div>
                            <div className="text-right text-sm">
                                <p className="font-semibold text-slate-700">{report.farmer_name || '—'}</p>
                                <p className="text-xs text-slate-400">
                                    {[report.field_name, report.field_code].filter(Boolean).join(' · ') || '—'}
                                </p>
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                            {valueRow('Submitted by', report.submitted_by_name)}
                            {valueRow('Workers', report.worker_count)}
                            {valueRow('Weather', report.weather_condition)}
                            {valueRow('Equipment condition', report.equipment_condition)}
                        </div>
                    </div>

                    {dailyReport && (
                        <div className="space-y-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">Daily Farmer Report</p>
                                    <h3 className="text-lg font-black text-slate-900">
                                        {dailyReport.farmer_information?.farmer_name || report.farmer_name || 'Farmer'} · {dailyReport.assignment_information?.field || report.field_name || 'Field'}
                                    </h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {allowExport && (
                                        <button
                                            onClick={() => handleExport('print')}
                                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-black text-white shadow-sm hover:bg-emerald-800"
                                        >
                                            <PrinterIcon className="h-4 w-4" />
                                            Printable report
                                        </button>
                                    )}
                                    {canManage && onCreateFollowUp && (
                                        <button
                                            onClick={() => onCreateFollowUp(report, dailyReport)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-black text-emerald-700 hover:bg-emerald-50"
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Create follow-up
                                        </button>
                                    )}
                                </div>
                            </div>

                            <DailyReportSection title="Farmer Information" data={dailyReport.farmer_information} columns={3} />
                            <DailyReportSection title="Assignment Information" data={dailyReport.assignment_information} columns={3} />
                            <DailyReportSection title="Work Performed" data={dailyReport.work_performed} columns={3} />
                            <DailyReportSection title="Materials and Equipment" data={dailyReport.materials_and_equipment} columns={3} />
                            <DailyReportSection title="Field Conditions and Observations" data={dailyReport.field_conditions_and_observations} columns={3} />
                            <DailyReportSection title="Issues and Follow-Up" data={dailyReport.issues_and_follow_up} columns={2} />
                            <DailyReportSection title="Evidence" data={dailyReport.evidence} columns={2} />
                            <DailyReportSection title="Management Review" data={dailyReport.management_review} columns={2} />
                        </div>
                    )}

                    {planned && actual && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Planned vs actual</p>
                            <PlannedVsActualPanel
                                planned={planned}
                                actual={actual}
                                variancePercent={report.variance_percent}
                                varianceExplanation={report.variance_explanation}
                            />
                        </div>
                    )}

                    <MaterialLotReviewPanel
                        planned={detail?.planned_materials}
                        reported={detail?.reported_materials}
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 p-4">
                            <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Execution</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                {valueRow('Started', formatDateTime(report.actual_start_time))}
                                {valueRow('Ended', formatDateTime(report.actual_end_time))}
                                {valueRow('Working time', report.total_working_minutes ? `${report.total_working_minutes} min` : undefined)}
                                {valueRow('Break', report.break_duration_minutes ? `${report.break_duration_minutes} min` : undefined)}
                                {valueRow('Area completed', report.area_completed ? `${Number(report.area_completed).toFixed(2)} ${report.area_unit || 'ha'}` : undefined)}
                                {valueRow('Quantity used', report.quantity_used ? `${Number(report.quantity_used).toFixed(2)} ${report.quantity_unit || ''}`.trim() : undefined)}
                                {valueRow('Calculated rate', report.calculated_rate ? Number(report.calculated_rate).toFixed(2) : undefined)}
                                {valueRow('Farmer confirmation', report.farmer_confirmation ? 'Confirmed' : 'Not confirmed')}
                            </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 p-4">
                            <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Findings</p>
                            <div className="grid grid-cols-1 gap-y-3">
                                {valueRow('Result summary', report.result_summary)}
                                {valueRow('Problem encountered', report.problem_encountered)}
                                {valueRow('Corrective action', report.corrective_action)}
                                {valueRow('Remaining work', report.remaining_work)}
                                {valueRow('Field condition', report.field_condition)}
                                {valueRow('Comments', report.comments)}
                                {report.assistance_required ? (
                                    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold text-amber-800">Assistance required</p>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {(report.start_gps_lat || report.completion_gps_lat) && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">GPS track</p>
                            <FieldMiniMap
                                field={field}
                                markers={[
                                    report.start_gps_lat
                                        ? { kind: 'start', label: 'S', lat: report.start_gps_lat, lng: report.start_gps_lng }
                                        : null,
                                    report.completion_gps_lat
                                        ? { kind: 'end', label: 'E', lat: report.completion_gps_lat, lng: report.completion_gps_lng }
                                        : null,
                                ].filter((m): m is { kind: 'start' | 'end'; label: string; lat: number; lng: number } => Boolean(m))}
                            />
                            <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-500 sm:grid-cols-2">
                                {report.start_gps_lat && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPinIcon className="h-3.5 w-3.5 text-emerald-500" />
                                        Start {Number(report.start_gps_lat).toFixed(5)}, {Number(report.start_gps_lng).toFixed(5)}
                                        {report.gps_accuracy_m ? ` ±${Number(report.gps_accuracy_m).toFixed(0)}m` : ''}
                                    </span>
                                )}
                                {report.completion_gps_lat && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPinIcon className="h-3.5 w-3.5 text-red-500" />
                                        Completion {Number(report.completion_gps_lat).toFixed(5)}, {Number(report.completion_gps_lng).toFixed(5)}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Photo evidence</p>
                        <EvidenceGallery groups={evidenceGroups} />
                    </div>

                    {observations.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Observations ({observations.length})</p>
                            {observations.some((o: any) => Number.isFinite(Number(o.gps_lat)) && Number.isFinite(Number(o.gps_lng))) && (
                                <div className="mb-3">
                                    <p className="mb-1 text-[11px] font-bold text-slate-500">Sampling points on field</p>
                                    <FieldMiniMap
                                        field={field}
                                        height={220}
                                        markers={observations.map((o: any) => ({
                                            kind: 'obs' as const,
                                            label: String(o.sample_point_number ?? ''),
                                            lat: Number(o.gps_lat),
                                            lng: Number(o.gps_lng),
                                            color:
                                                o.severity_level === 'critical'
                                                    ? '#e11d48'
                                                    : o.severity_level === 'high'
                                                    ? '#f97316'
                                                    : o.severity_level === 'medium'
                                                    ? '#fbbf24'
                                                    : o.severity_level === 'low'
                                                    ? '#10b981'
                                                    : '#f59e0b',
                                        }))}
                                    />
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {observations.map((o: any) => (
                                            <span
                                                key={o.id}
                                                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                                                title={`${o.severity_level || 'info'} · ${o.gps_lat}, ${o.gps_lng}`}
                                            >
                                                <span
                                                    className={`h-2 w-2 rounded-full ${
                                                        o.severity_level === 'critical'
                                                            ? 'bg-rose-600'
                                                            : o.severity_level === 'high'
                                                            ? 'bg-orange-500'
                                                            : o.severity_level === 'medium'
                                                            ? 'bg-amber-400'
                                                            : o.severity_level === 'low'
                                                            ? 'bg-emerald-500'
                                                            : 'bg-slate-400'
                                                    }`}
                                                />
                                                {o.sample_point_number != null ? `#${o.sample_point_number}` : 'point'}
                                                {o.pest_type || o.disease_type ? ` · ${o.pest_type || o.disease_type}` : ''}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <ObservationList observations={observations} />
                        </div>
                    )}

                    {reviews.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Review history</p>
                            <div className="space-y-2">
                                {reviews.map((review: ActivityReview) => (
                                    <div key={review.id} className="rounded-xl border border-slate-200 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-black capitalize text-slate-800">{String(review.decision).replace(/_/g, ' ')}</span>
                                            <span className="text-xs text-slate-400">{formatDateTime(review.reviewed_at)}</span>
                                        </div>
                                        {review.correction_instructions && <p className="mt-1 text-sm text-orange-700">{review.correction_instructions}</p>}
                                        {review.review_comment && <p className="mt-1 text-sm text-slate-600">{review.review_comment}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {canReview && !activeAction && (
                        <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <button
                                onClick={() => setActiveAction('approve')}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-emerald-700"
                            >
                                <CheckCircleIcon className="h-4 w-4" />
                                Approve
                            </button>
                            <button
                                onClick={() => setActiveAction('correct')}
                                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-orange-700"
                            >
                                <ArrowUturnLeftIcon className="h-4 w-4" />
                                Request correction
                            </button>
                            <button
                                onClick={() => setActiveAction('reject')}
                                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-red-700"
                            >
                                <XCircleIcon className="h-4 w-4" />
                                Reject
                            </button>
                        </div>
                    )}

                    {activeAction && (
                        <div className={`rounded-xl border p-4 ${activeAction === 'approve' ? 'border-emerald-200 bg-emerald-50/50' : activeAction === 'correct' ? 'border-orange-200 bg-orange-50/50' : 'border-red-200 bg-red-50/50'}`}>
                            <p className="text-sm font-black text-slate-900">{actionCopy[activeAction].title}</p>
                            <p className="mt-1 text-sm text-slate-600">{actionCopy[activeAction].body}</p>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={actionCopy[activeAction].placeholder}
                                rows={3}
                                className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                            />
                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setActiveAction(null);
                                        setNote('');
                                        setActionError('');
                                    }}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={() => runAction(activeAction)}
                                    disabled={busy}
                                    className={`rounded-lg px-4 py-1.5 text-xs font-black shadow-sm disabled:opacity-60 ${actionCopy[activeAction].color}`}
                                >
                                    {busy ? 'Working…' : actionCopy[activeAction].button}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {allowExport && report && (
                <div className="mt-6 flex flex-wrap gap-2">
                    <button
                        onClick={() => handleExport('json')}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Export JSON
                    </button>
                    <button
                        onClick={() => handleExport('csv')}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Export CSV
                    </button>
                    <button
                        onClick={() => handleExport('print')}
                        className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-100"
                    >
                        <PrinterIcon className="h-4 w-4" />
                        Print / PDF
                    </button>
                </div>
            )}
        </ActivityDrawer>
    );
};

export default ReportReviewDrawer;
